import Button from 'common/components/Button/Button';
import Card from 'common/components/Card/Card';
import FormInput from 'common/components/FormInput/FormInput';
import FormScrollError from 'common/components/FormScrollError/FormScrollError';
import useAuth from 'common/contexts/auth';
import arrayMutators from 'final-form-arrays';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Form } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { useTranslation } from 'react-i18next';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import CompanySelect from 'requests/components/CompanySelect';
import useCompanies from 'requests/contexts/companies';

const DragHandle = SortableHandle(() => (
  <span className="icon" style={{ cursor: 'move' }}>
    <i className="fas fa-ellipsis-v" />
  </span>
));

const SortableItem = SortableElement(({
  value, name, fields,
}) => {
  const { t } = useTranslation();

  return (
    <div className="sortable-item">
      <div className="sortable-item-start">
        <DragHandle />
        <Button
          onClick={() => fields.remove(value)}
          icon="fa-trash-alt"
          color="danger"
          confirm
          transparent
          confirmMessage={t('common.delete')}
          style={{ padding: 0 }}

        />
      </div>

      <div className="sortable-item-main">
        <FormInput
          name={`${name}.name`}
          placeholder={t('common.name')}
          required
          className="list-item-field"
        />

        <FormInput
          name={`${name}.description`}
          placeholder={t('todolist.description')}
          className="list-item-field"
        />
      </div>

    </div>
  );
});

const SortableList = SortableContainer(({ items }) => (
  <div className="sortable-subform">
    {items && items.map((name, index) => (
      <SortableItem
        key={`item-${index}`}
        index={index}
        value={index}
        name={name}
        fields={items}
      />
    ))}
  </div>
));

const sortEnd = move => ({ oldIndex, newIndex }) => {
  move(oldIndex, newIndex);
};

const TodoTemplateForm = ({ onSubmit, todo }) => {
  const { t } = useTranslation();
  const { user: currentUser, isAdmin, isManager } = useAuth();
  const { currentCompany } = useCompanies();
  const canEdit = (isAdmin || isManager || currentUser?.id === todo?.author);

  const initialValues = useMemo(() => todo?.id ? {
    ...todo,
    company: todo?.company?.id,
  } : {
    name: '',
    company: currentCompany.id,
    author: currentUser.id,
  }, [todo, currentCompany, currentUser]);

  return (
    <Form
      onSubmit={onSubmit}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
      }}
      render={({
        handleSubmit, submitting, pristine,
      }) => (
        <form onSubmit={handleSubmit} noValidate className="todo-form">
          <FormScrollError />
          <div className="columns">
            <div className="column if-two-thirds">
              <Card>
                <div className="card-custom-header">
                  <h2 className="title is-2">
                    {t('todolist.editMainTask')}
                  </h2>
                </div>
                { !currentCompany && currentUser?.role?.name === 'superadmin' && (
                <FormInput
                  type="custom"
                  name="company"
                  label={t('common.company')}
                >
                  <CompanySelect />
                </FormInput>
                )}

                <FormInput
                  name="name"
                  label={t('common.name')}
                  disabled={!canEdit}
                  required
                />
                <FormInput
                  name="description"
                  type="richtext"
                  placeholder={t('todolist.description')}
                  disabled={!canEdit}
                  required
                />

              </Card>
            </div>

            <div className="column is-one-third">
              <Card title={t('todolist.editSubTasks')} prepareScrollRight>
                <FieldArray name="subitems">
                  {({ fields }) => (

                    <div className="sortable-box">
                      <div className="card-scroller">
                        <SortableList
                          items={fields}
                          useDragHandle
                          onSortEnd={sortEnd(fields.move)}
                        />
                      </div>

                      <Button
                        onClick={() => fields.push({})}
                        icon="fa-plus"
                        label={t('todolist.addItem')}
                        className="button is-light"
                      />
                    </div>
                  )}
                </FieldArray>

              </Card>
            </div>
          </div>

          <div className="stickySubmit">
            <FormInput
              type="submit"
              label={t('common.send')}
              icon="paper-plane"
              disabled={submitting || pristine}
              isSubmitting={submitting}
            />
          </div>

        </form>
      )}
    />
  );
};

TodoTemplateForm.propTypes = {
  onSubmit: PropTypes.func,
  todo: PropTypes.object,
};

TodoTemplateForm.defaultProps = {
  onSubmit() { },
  todo: {},
};

export default TodoTemplateForm;
